<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { hall } from '@/hall'
import { Providers, GameType } from '@/defines/EnumDefine'

const emit = defineEmits(['open-provider'])

interface ProviderDisplay {
  id: Providers
  name: string
  iconUrl: string
  gameCount: number
}

const displayProviders = ref<ProviderDisplay[]>([])
const scrollContainer = ref<HTMLElement | null>(null)

const getProviderIcon = (iconName: string) => {
  const map: Record<string, string> = {
    big_provider_pgsoft: 'slot-pgsoft.webp',
    big_provider_jili: 'slot-jili.webp',
    big_provider_spribe: 'slot-spribe.webp',
    big_provider_cq9: 'slot-cq9.webp',
    big_provider_evolive: 'slot-evolution.webp',
    big_provider_pp: 'slot-pragmatic.webp',
    big_provider_jdb: 'slot-jdb.webp',
    big_provider_tada: 'slot-tada.webp',
    big_provider_red: 'slot-redtiger2.webp',
    big_provider_bng: 'slot-bng.webp',
    big_provider_nextspin: 'slot-nextspin.webp',
    big_provider_wonwon: 'slot-wonwon.webp',
    big_provider_aog: 'slot-aog.webp',
    big_provider_v8: 'slot-v8.webp',
    big_provider_fachai: 'slot-fachai.webp',
    big_provider_hacksaw: 'slot-hacksaw.webp',
    big_provider_bgaming: 'slot-bgaming.webp',
    big_provider_InOut: 'slot-inOut.webp',
    big_provider_KoolBet: 'slot-koolBet.webp',
    big_provider_Mini: 'slot-mini.webp',
  }
  const filename = map[iconName] || 'slot-pgsoft.webp'
  return new URL(`../assets/hall/providers/${filename}`, import.meta.url).href
}

const refreshProviders = () => {
  const infos = hall.hallModule.PROVIDER_TYPE_INFOS as any
  const list: ProviderDisplay[] = []

  // Skip "All" provider (index 0 usually)
  for (const key in infos) {
    const providerEnum = Number(key)
    if (!isNaN(providerEnum) && providerEnum !== Providers.All) {
      const info = infos[key]
      const count = hall.hallModule.getProviderGameAmount(GameType.All, providerEnum) || 0
      list.push({
        id: providerEnum,
        name: info.name,
        iconUrl: getProviderIcon(info.icon),
        gameCount: count,
      })
    }
  }

  // Sort them loosely based on some factor or just leave as is
  displayProviders.value = list
  hall.eventDispatcher.emit(hall.EventDefine.ChangeBindActivityRed) //刷新红点
}

const scrollLeft = () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollBy({ left: -200, behavior: 'smooth' })
  }
}

const scrollRight = () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollBy({ left: 200, behavior: 'smooth' })
  }
}

const openProvider = (providerId: Providers) => {
  emit('open-provider', providerId)
}

onMounted(() => {
  refreshProviders()
  hall.eventDispatcher.on(hall.EventDefine.ChangeProviderList, refreshProviders)
})

onUnmounted(() => {
  hall.eventDispatcher.off(hall.EventDefine.ChangeProviderList, refreshProviders)
})
</script>

<template>
  <section class="provider-category-section">
    <div class="section-title">
      <div class="category-title"><span class="cat-icon">🤝</span> Providers</div>
      <div class="controls">
        <button class="ctrl-btn" @click="scrollLeft">&lt;</button>
        <button class="ctrl-btn" @click="scrollRight">&gt;</button>
        <button class="ctrl-btn all-btn">All</button>
      </div>
    </div>

    <div class="providers-scroll-container" ref="scrollContainer">
      <div class="providers-grid">
        <div
          v-for="provider in displayProviders"
          :key="provider.id"
          class="provider-card cursor-pointer"
          @click="openProvider(provider.id)"
        >
          <div class="logo-wrapper">
            <img :src="provider.iconUrl" class="provider-img" :alt="provider.name" />
          </div>
          <div class="game-count-tag">
            <span>🎮 {{ provider.gameCount }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.provider-category-section {
  padding: 0 10px;
  margin-bottom: 20px;
  background-color: transparent;
  padding-top: 10px;
}

.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.category-title {
  font-size: 1.2rem;
  font-weight: bold;
}

.cat-icon {
  margin-right: 5px;
}

.controls {
  display: flex;
  gap: 5px;
}

.ctrl-btn {
  background: #333;
  border: none;
  color: #aaa;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
}

.all-btn {
  background: #444;
}

.providers-scroll-container {
  position: relative;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 10px;
  scrollbar-width: none;
}

.providers-scroll-container::-webkit-scrollbar {
  display: none;
}

.providers-grid {
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-auto-flow: column;
  grid-auto-columns: calc((100vw - 20px - 30px) / 4);
  gap: 10px;
  min-height: 160px;
}

.provider-card {
  aspect-ratio: 1.4;
  border-radius: 10px;
  background: #2a2a2a;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.logo-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  width: 100%;
}

.provider-img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

.game-count-tag {
  width: 100%;
  text-align: center;
  background: rgba(0, 0, 0, 0.6);
  color: #bbb;
  font-size: 0.8rem;
  padding: 4px 0;
  font-weight: bold;
}

.cursor-pointer {
  cursor: pointer;
}

/* ===== DESKTOP: game-icon size, no controls ===== */
@media (min-width: 768px) {
  .provider-category-section {
    max-width: 100%;
  }

  .providers-grid {
    /* Single row, same column width as game icons */
    grid-template-rows: 1fr;
    grid-auto-columns: minmax(140px, 170px);
    gap: 12px;
    min-height: unset;
  }

  .provider-card {
    aspect-ratio: unset;
    height: 150px; /* same as .game-img height */
  }
}

@media (min-width: 1200px) {
  .providers-grid {
    grid-auto-columns: minmax(155px, 185px);
  }

  .provider-card {
    height: 170px;
  }
}
</style>
